import {
  useStoryblokState,
  getStoryblokApi,
  StoryblokComponent,
} from "@storyblok/react";
import { GetStaticPropsContext } from "next";
import BackgroundGraphic from "../../components/BackgroundGraphic";
import OrderOfMeritPageTabs from "../../components/OrderOfMeritPageTabs/OrderOfMeritPageTabs";
import fs from "fs";
import path from "path";
import Papa from "papaparse";
import OOMLeaderboard from "../../components/OOMLeaderboard";

interface CsvRow {
  [key: string]: string | number;
}

interface CsvFile {
  name: string;
  data: CsvRow[];
}

export default function OrderOfMeritPage({ story, tabs, csvFiles }: any) {
  story = useStoryblokState(story);

  return (
    <>
      <BackgroundGraphic align="right" />
      <OrderOfMeritPageTabs tabs={tabs}>
        {story?.slug === "rankings" && (
          <OOMLeaderboard
            key={story._uid}
            blok={story?.content?.body?.[0]}
            csvFiles={csvFiles}
          />
        )}
        {story?.slug !== "rankings" && (
          <StoryblokComponent key={story._uid} blok={story.content} />
        )}
      </OrderOfMeritPageTabs>
    </>
  );
}

export async function getStaticPaths() {
  const storyblokApi = getStoryblokApi();
  const { data } = await storyblokApi.get("cdn/links/", {
    version: process.env.VERCEL_ENV === "production" ? "published" : "draft",
  });

  let paths: any = [];
  Object.keys(data.links).forEach((linkKey) => {
    if (
      data.links[linkKey].is_folder ||
      data.links[linkKey].slug === "order-of-merit" ||
      data.links[linkKey].slug === "order-of-merit/" ||
      !data.links[linkKey].slug.includes("order-of-merit/")
    ) {
      return;
    }

    const slug = data.links[linkKey].slug;
    let splittedSlug = slug
      .split("/")
      .filter((slug: string) => slug !== "order-of-merit");

    paths.push({
      params: { slug: splittedSlug },
    });
  });

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const { params } = context;

  const paramsSlug =
    params?.slug && Array.isArray(params?.slug) && params?.slug.length > 1
      ? params.slug.join("/")
      : params?.slug;

  const sbParams = {
    version: process.env.VERCEL_ENV === "production" ? "published" : "draft",
    resolve_links: "url",
  };

  const storyblokApi = getStoryblokApi();
  const { data } = await storyblokApi.get(
    `cdn/stories/order-of-merit/${paramsSlug || ""}`,
    sbParams
  );

  const { data: oomStoriesData } = await storyblokApi.get(`cdn/stories/`, {
    ...sbParams,
    starts_with: "order-of-merit",
  });

  const tabs = oomStoriesData?.stories?.map((story: any) => ({
    value: story?.name,
    label: story?.name,
    slug: story?.slug,
  }));

  const publicPath = path.join(process.cwd(), "public/csv");
  const files = fs
    .readdirSync(publicPath)
    .filter((file) => file.endsWith(".csv"));

  const csvFiles: CsvFile[] = files.map((file) => {
    const filePath = path.join(publicPath, file);
    const csvText = fs.readFileSync(filePath, "utf-8");

    let parsedData: CsvRow[] = [];
    Papa.parse(csvText, {
      header: false,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (result) => {
        parsedData = result?.data as CsvRow[];
      },
    });

    return {
      name: file,
      data: parsedData,
    };
  });

  return {
    props: {
      story: data ? data.story : false,
      key: data ? data.story.id : false,
      tabs,
      csvFiles,
    },
    revalidate: 10,
  };
}
